// This composable is used to get transportation fields for all forms
import type { FieldValueResult } from '@/components/documents/Foxit/FormMapping/composables/FieldValueProvider'
import type { MappedCartItem } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useFormFillHelper } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'

export const useTransportationFields = () => {
	const {
		findCartItems,
		getFieldPrice,
		getFieldDescription,
		getAttributeValue,
		multilineWithSingleTotalFields,
	} = useFormFillHelper()

	const utilityVehicleVanDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationUtilityVehicleVan
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const utilityVehicleVanPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationUtilityVehicleVan
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const utilityVehicleVanFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Transportation,
			PbSubcategory.TransportationUtilityVehicleVan
		)

		return [
			...utilityVehicleVanDesc(fieldNames.description, items),
			...utilityVehicleVanPrice(fieldNames.price, items),
		]
	}

	const hearseDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationHearse
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const hearsePrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationHearse
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const hearseMileRadiusPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => hearsePrice(fieldName, passedItems)

	const hearseFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Transportation,
			PbSubcategory.TransportationHearse
		)

		return [
			...hearseDesc(fieldNames.description, items),
			...hearsePrice(fieldNames.price, items),
		]
	}

	const transferMileRadiusPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationTransfer
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const transferMileRadiusValue = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationTransfer
			)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'mileRadiusForStandardService'),
				format: false,
				// unique section to avoid considering during totals
				section: 'service-value',
			},
		]
	}

	const transferMileRadiusFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Transportation,
			PbSubcategory.TransportationTransfer
		)

		return [
			...transferMileRadiusPrice(fieldNames.price, items),
			...transferMileRadiusValue(fieldNames.value, items),
		]
	}

	const transportationOtherPrice = (fieldName: string): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Transportation,
			PbSubcategory.Other
		)
		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const leadCarDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationLeadCar
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const leadCarPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationLeadCar
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const leadCarFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Transportation,
			PbSubcategory.TransportationLeadCar
		)

		return [
			...leadCarDesc(fieldNames.description, items),
			...leadCarPrice(fieldNames.price, items),
		]
	}

	const familyCarLimoDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationFamilyCarLimo
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const familyCarLimoPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Transportation,
				PbSubcategory.TransportationFamilyCarLimo
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const familyCarLimoFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Transportation,
			PbSubcategory.TransportationFamilyCarLimo
		)

		return [
			...familyCarLimoDesc(fieldNames.description, items),
			...familyCarLimoPrice(fieldNames.price, items),
		]
	}

	const automotiveOtherFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Transportation,
			PbSubcategory.TransportationAutomotiveOther
		)

		return multilineWithSingleTotalFields(items, fieldNames, 'service')
	}

	return {
		utilityVehicleVanDesc,
		utilityVehicleVanPrice,
		utilityVehicleVanFields,
		hearseDesc,
		hearsePrice,
		hearseMileRadiusPrice,
		hearseFields,
		transferMileRadiusPrice,
		transferMileRadiusValue,
		transferMileRadiusFields,
		leadCarDesc,
		leadCarPrice,
		leadCarFields,
		familyCarLimoDesc,
		familyCarLimoPrice,
		familyCarLimoFields,
		transportationOtherPrice,
		automotiveOtherFields,
	}
}
